import { organizationApi } from 'src/api/organizationApi';
import type { WazuhSeverityCount } from 'src/types/mindtime/wazuhDashboard';
import logger from 'src/utils/logger';

const SEVERITY_ORDER = ['Critical', 'High', 'Medium', 'Low'] as const;

/** Map API severity series to fixed-order count arrays for SecuritySeverityCompareBarChart. */
export const severitySeriesToCounts = (series: WazuhSeverityCount[]): number[] => {
  const map = new Map(series.map((item) => [item.severity, item.count]));
  return SEVERITY_ORDER.map((label) => map.get(label) ?? 0);
};

export const SEVERITY_CHART_LABELS = [...SEVERITY_ORDER];

/** Derive agent health label from agent-overview counts (per API doc). */
export const deriveAgentHealthLabel = (active: number, warning: number, offline: number): string => {
  const total = active + warning + offline;
  if (total <= 0) return 'Unknown';
  const uptimePct = (active / total) * 100;
  if (uptimePct >= 99) return 'Excellent';
  if (uptimePct >= 95) return 'Good';
  return 'Needs Attention';
};

export const formatRelativeTime = (isoTime: string): string => {
  const timestamp = new Date(isoTime).getTime();
  if (Number.isNaN(timestamp)) return isoTime;

  const diffMs = Date.now() - timestamp;
  const diffMinutes = Math.floor(diffMs / 60000);
  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;

  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours}h ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d ago`;
};

export const formatImpactPercent = (value: number): string => {
  const sign = value > 0 ? '+' : '';
  return `Impact ${sign}${value}%`;
};

const orgNameCache = new Map<string, string>();

/**
 * Resolve an organization's display name. Uses the in-memory scope cache first
 * (populated by `useWazuhScope`), falls back to `organizationApi.getById` once.
 */
export const resolveOrganizationName = async (
  orgId: string,
  getOrganizationName: (id: string) => string | undefined
): Promise<string> => {
  const cached = getOrganizationName(orgId) ?? orgNameCache.get(orgId);
  if (cached) return cached;

  try {
    const response = await organizationApi.getById(orgId);
    const name = response?.data?.name ?? response?.data?.data?.name;
    if (typeof name === 'string' && name.trim()) {
      orgNameCache.set(orgId, name);
      return name;
    }
  } catch (error) {
    logger(error, 'error', 'resolveOrganizationName');
  }

  return orgId;
};
